import React from 'react';
import { CONSTANTS } from '../../data';
import { ASSETS } from '../../assetManifest.js';
import { Trash2 } from 'lucide-react';

interface BrewSlotsProps {
  slots: (string | null)[];
  onRemove: (slotIndex: number) => void;
  allItemsMap: Map<string, any>;
  containerWidth: number;
  containerPadding: number;
  elementGap: number;
  isLargeScreen: boolean;
}

const BrewSlots: React.FC<BrewSlotsProps> = ({ 
  slots, 
  onRemove, 
  allItemsMap, 
  containerWidth,
  containerPadding,
  elementGap,
  isLargeScreen
}) => {
  const isMobile = !isLargeScreen;
  // Calculate responsive sizing based on container
  const slotHeight = isMobile ? 25 : Math.max(
    CONSTANTS.BREW_STATION_GRID.MIN_SLOT_HEIGHT, 
    containerWidth * CONSTANTS.BREW_STATION_GRID.SLOT_HEIGHT_RATIO
  );
  const slotTextSize = Math.max(
    CONSTANTS.BREW_STATION_GRID.MIN_TEXT_SIZE,
    containerWidth * CONSTANTS.BREW_STATION_GRID.TEXT_SIZE_RATIO
  );
  const slotPadding = Math.max(4, Math.floor(slotHeight * 0.1));

  const getSpritePath = (itemId: string) => {
    const sprite = (ASSETS as any).items?.[itemId]?.path 
      || (ASSETS as any).images?.defaultItem?.path 
      || (ASSETS as any).images?.logo?.path;
    return sprite;
  };

  const handleRemoveClick = (e: React.MouseEvent<HTMLButtonElement>, slotIndex: number) => {
    // Prevent bubbling to the slot and defer unmount to next task
    e.preventDefault();
    e.stopPropagation();
    setTimeout(() => {
      onRemove(slotIndex);
    }, 0);
  };

  return (
    <div
      data-tutorial-id={`brew-slots-0`}
      className="brew-slots flex"
      style={{
        gap: `${elementGap}px`,
        marginBottom: `${elementGap}px`
      }}
    >
      {[0, 1].map((slotIndex) => {
        const itemId = slots[slotIndex] || undefined;
        const itemName = itemId ? (allItemsMap.get(itemId)?.name || itemId) : '';

        return (
          <div key={slotIndex} className="flex-1 flex flex-col items-center" style={{ gap: `${Math.max(2, elementGap / 4)}px` }}>
            <div
              className={`brew-slot w-full border-2 border-dashed rounded-lg flex items-center justify-center bg-bg-main transition-all touch-manipulation relative ${
                !itemId
                  ? "border-border-color slot-pulse"
                  : "border-accent"
              } ${
                slots[0] && !slots[1] && slotIndex === 1
                  ? "slot-glow"
                  : ""
              }`}
              style={{
                height: `${Math.max(slotHeight, CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE)}px`,
                minHeight: `${CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE}px`,
                padding: `${slotPadding}px`,
              }}
              title={itemName}
            >
              {itemId ? (
                <img
                  src={getSpritePath(itemId)}
                  alt={itemName}
                  className="object-contain pointer-events-none select-none"
                  style={{
                    maxWidth: '90%',
                    maxHeight: '80%',
                  }}
                  draggable={false}
                />
              ) : (
                <div 
                  className="text-text-secondary text-center"
                  style={{
                    fontSize: `${Math.max(9, slotTextSize - 1)}px`,
                    lineHeight: 1.2,
                  }}
                >
                  {containerWidth < 200
                    ? (slotIndex === 0 ? "Item" : "Tool")
                    : (slotIndex === 0 ? "Ingredient" : "Tool or Drink")
                  }
                </div>
              )}
            </div>
            
            <div style={{ height: `${Math.max(16, containerWidth * 0.08)}px` }}>
              {itemId && (
                <button
                  type="button"
                  onClick={(e) => handleRemoveClick(e, slotIndex)}
                  className="text-danger hover:text-red-400 p-1 rounded-full hover:bg-danger/10 active:scale-95 transition-all flex items-center justify-center"
                  style={{
                    minHeight: 'auto', // override default button min-height
                    height: '100%',
                    aspectRatio: '1 / 1',
                  }}
                  aria-label={`Remove ${itemName}`}
                >
                  <Trash2 size="100%" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BrewSlots;
